<template>
  <div class="max-w-7xl mx-auto px-4 py-8">
    <Breadcrumbs :items="breadcrumbs" />

    <div class="mb-6">
      <div v-if="categoryLoading && !category" class="space-y-3">
        <USkeleton class="h-9 w-64" />
        <USkeleton class="h-4 w-full max-w-2xl" />
      </div>
      <template v-else>
        <div class="flex flex-wrap items-end justify-between gap-4">
          <h1 class="text-2xl md:text-3xl font-bold text-highlighted">
            {{ category?.name || slug }}
          </h1>
          <p v-if="pagination.total" class="shrink-0 text-sm text-muted">
            {{ products.length }} din {{ pagination.total }} produse
          </p>
        </div>
        <p v-if="category?.description" class="mt-2 text-toned max-w-3xl">
          {{ category.description }}
        </p>
      </template>
    </div>

    <component
      :is="bannerHref ? NuxtLinkComponent : 'div'"
      v-if="bannerSrc"
      v-bind="bannerHref ? { to: bannerHref, target: bannerExternal ? '_blank' : undefined } : {}"
      class="mb-6 block overflow-hidden rounded-2xl bg-elevated"
    >
      <img
        :src="bannerSrc"
        :alt="category?.name || ''"
        class="h-auto max-h-80 w-full object-cover"
        fetchpriority="high"
      >
    </component>

    <AdvantagesBanner class="mb-6" />

    <ProductMobileToolbar
      :filters="filters"
      :brands="productsStore.brands"
      :attributes="productsStore.attributes"
      @update="onFilterUpdate"
    />

    <ProductFilterChips
      v-if="activeFilterCount > 0"
      class="lg:hidden"
      :filters="filters"
      :brands="productsStore.brands"
      :attributes="productsStore.attributes"
      scrollable
      @remove="onChipRemove"
    />

    <ProductFilterChips
      class="hidden lg:block"
      :filters="filters"
      :brands="productsStore.brands"
      :attributes="productsStore.attributes"
      @remove="onChipRemove"
    />

    <div class="grid grid-cols-1 gap-8 lg:grid-cols-4">
      <aside class="hidden lg:col-span-1 lg:block">
        <ProductFilters :filters="filters" @update="onFilterUpdate" />
      </aside>

      <div class="lg:col-span-3">
        <ProductGrid
          :products="products"
          :loading="loading || !productsFetched"
          :loading-more="loadingMore"
        />

        <div
          v-if="hasMoreProducts"
          ref="loadMoreTrigger"
          class="flex flex-col items-center justify-center gap-2 py-8"
        >
          <UIcon
            v-if="loadingMore"
            name="i-heroicons-arrow-path"
            class="h-6 w-6 animate-spin text-brand-600"
          />
          <p v-if="loadingMore" class="text-sm text-muted">Se încarcă mai multe produse...</p>
        </div>

        <p
          v-else-if="products.length > 0"
          class="py-8 text-center text-sm text-dimmed"
        >
          Ai văzut toate produsele
        </p>
      </div>
    </div>

    <section v-if="longDescriptionHtml" class="mt-10 border-t border-default pt-8">
      <div class="relative">
        <div
          ref="longDescriptionEl"
          class="prose-cms max-w-none overflow-hidden"
          :class="longDescriptionOpen ? '' : 'max-h-48'"
          v-html="longDescriptionHtml"
        />
        <div
          v-if="!longDescriptionOpen && longDescriptionOverflows"
          class="category-long-description-fade pointer-events-none absolute inset-x-0 bottom-0 h-20"
        />
      </div>
      <UButton
        v-if="longDescriptionOverflows"
        :label="longDescriptionOpen ? 'Citește mai puțin' : 'Citește mai mult'"
        :trailing-icon="longDescriptionOpen ? 'i-heroicons-chevron-up' : 'i-heroicons-chevron-down'"
        variant="link"
        class="mt-2 px-0"
        @click="longDescriptionOpen = !longDescriptionOpen"
      />
    </section>
  </div>
</template>

<script setup lang="ts">
import type { Category, ProductFilterState } from '~/types'
import {
  countActiveProductFilters,
  parseProductFiltersFromQuery,
  productFiltersToQuery,
} from '~/utils/productFilters'
import { enrichHtmlMediaUrls } from '~/utils/htmlMedia'

const route = useRoute()
const router = useRouter()
const productsStore = useProductsStore()
const { productsDefaultSort } = useSiteSettings()
const { get } = useApi()
const { mediaUrl } = useMediaUrl()
const shopPath = useShopPath()
const config = useRuntimeConfig()
const siteUrl = config.public.siteUrl as string
const apiOrigin = config.public.apiOrigin as string

const slug = computed(() => route.params.slug as string)

const { data: category, pending: categoryLoading } = await useAsyncData(
  () => `category-${slug.value}`,
  async () => {
    const response = await get<Category>(`/categories/${slug.value}`)
    return response.success && response.data ? response.data : null
  },
  { watch: [slug] },
)

const products = computed(() => productsStore.products)
const loading = computed(() => productsStore.loading)
const loadingMore = computed(() => productsStore.loadingMore)
const productsFetched = computed(() => productsStore.productsFetched)
const hasMoreProducts = computed(() => productsStore.hasMoreProducts)
const pagination = computed(() => productsStore.pagination)
const filters = computed(() => productsStore.filters)
const activeFilterCount = computed(() => countActiveProductFilters(filters.value))

const loadMoreTrigger = ref<HTMLElement | null>(null)

const NuxtLinkComponent = resolveComponent('NuxtLink')
const bannerSrc = computed(() => (category.value?.bannerImage ? mediaUrl(category.value.bannerImage) : ''))
const bannerExternal = computed(() => /^https?:\/\//i.test(category.value?.bannerLink || ''))
const bannerHref = computed(() => {
  const link = category.value?.bannerLink?.trim()
  if (!link) return ''
  return bannerExternal.value ? link : shopPath(link)
})

const longDescriptionHtml = computed(() =>
  category.value?.longDescription ? enrichHtmlMediaUrls(category.value.longDescription, apiOrigin) : '',
)
const longDescriptionEl = ref<HTMLElement | null>(null)
const longDescriptionOpen = ref(false)
// Assume long text on SSR so the toggle is in the first paint; measured after mount.
const longDescriptionOverflows = ref(true)

function measureLongDescription() {
  const el = longDescriptionEl.value
  if (!el || longDescriptionOpen.value) return
  longDescriptionOverflows.value = el.scrollHeight > el.clientHeight + 4
}

onMounted(measureLongDescription)
watch(longDescriptionHtml, () => {
  longDescriptionOpen.value = false
  longDescriptionOverflows.value = true
  nextTick(measureLongDescription)
})

const breadcrumbs = computed(() => [
  { label: 'Produse', to: '/produse' },
  { label: category.value?.name || slug.value },
])

useSeo({
  title: category.value?.metaTitle || category.value?.name || slug.value,
  description: category.value?.metaDescription
    || category.value?.description
    || `Produse din categoria ${category.value?.name || slug.value}`,
  jsonLd: category.value
    ? siteBreadcrumbJsonLd([
        { name: 'Produse', url: `${siteUrl}/produse` },
        { name: category.value.name, url: `${siteUrl}/categorii/${category.value.slug}` },
      ], siteUrl)
    : undefined,
})

useIntersectionObserver(
  loadMoreTrigger,
  ([entry]) => {
    if (entry?.isIntersecting) {
      productsStore.loadMoreProducts()
    }
  },
  { rootMargin: '240px' },
)

async function loadProducts(params?: ProductFilterState) {
  await productsStore.fetchProducts({
    ...params,
    categoryId: category.value?.id,
    page: 1,
  })
}

function onFilterUpdate(newFilters: ProductFilterState) {
  // categoryId stays implicit in the route, keep it out of the query string
  const query = productFiltersToQuery(
    { ...newFilters, categoryId: undefined, page: 1 },
    productsDefaultSort.value,
  )
  router.push({ query })
  loadProducts(newFilters)
}

function onChipRemove(key: string) {
  const nextFilters: ProductFilterState = { ...filters.value, page: 1 }

  if (key.startsWith('brand-')) {
    const brandId = Number(key.replace('brand-', ''))
    nextFilters.brandIds = (nextFilters.brandIds || []).filter((id) => id !== brandId)
    if (!nextFilters.brandIds.length) {
      nextFilters.brandIds = undefined
      nextFilters.models = undefined
    }
  } else if (key.startsWith('model-')) {
    const model = decodeURIComponent(key.replace('model-', ''))
    nextFilters.models = (nextFilters.models || []).filter((item) => item !== model)
    if (!nextFilters.models.length) nextFilters.models = undefined
  } else if (key.startsWith('attr-')) {
    const valueId = Number(key.split('-').pop())
    nextFilters.attributeValueIds = (nextFilters.attributeValueIds || []).filter((id) => id !== valueId)
    if (!nextFilters.attributeValueIds.length) nextFilters.attributeValueIds = undefined
  } else if (key.startsWith('color-')) {
    const color = decodeURIComponent(key.replace('color-', ''))
    nextFilters.colors = (nextFilters.colors || []).filter((item) => item !== color)
    if (!nextFilters.colors.length) nextFilters.colors = undefined
  } else if (key.startsWith('storage-')) {
    const storage = decodeURIComponent(key.replace('storage-', ''))
    nextFilters.storages = (nextFilters.storages || []).filter((item) => item !== storage)
    if (!nextFilters.storages.length) nextFilters.storages = undefined
  } else if (key === 'inStock') {
    nextFilters.inStock = false
  } else if (key.startsWith('availability-')) {
    const statusId = Number(key.replace('availability-', ''))
    nextFilters.availabilityStatusIds = (nextFilters.availabilityStatusIds || []).filter((id) => id !== statusId)
    if (!nextFilters.availabilityStatusIds.length) nextFilters.availabilityStatusIds = undefined
  } else {
    nextFilters[key as keyof ProductFilterState] = undefined as never
  }

  onFilterUpdate(nextFilters)
}

async function initCategoryProducts() {
  const queryFilters = parseProductFiltersFromQuery(
    route.query as Record<string, unknown>,
    productsDefaultSort.value,
  )

  if (import.meta.client && route.query.page) {
    const query = productFiltersToQuery({ ...queryFilters, page: 1 }, productsDefaultSort.value)
    await router.replace({ query })
  }

  await Promise.all([
    productsStore.attributes.length ? Promise.resolve() : productsStore.fetchAttributes(),
    loadProducts(queryFilters),
  ])
}

watch(slug, () => {
  initCategoryProducts()
})

await initCategoryProducts()
</script>

<style scoped>
.category-long-description-fade {
  background: linear-gradient(to top, var(--ui-bg, #ffffff), transparent);
}
</style>
